/**
 * useModuleBootstrap - Minimal bootstrap for module routes
 * 
 * CRITICAL: This hook does NOT load FreeWorld data (time periods, tasks, events).
 * It only loads what's necessary for the module:
 * - Session validation
 * - Player identity
 * - Quiz progress (via centralized cache)
 * 
 * For FreeWorld routes, use usePlayerCoreInitializer instead.
 */

import { useEffect, useRef } from 'react';
import { usePlayerAuth } from './usePlayerAuth';

// Track if bootstrap has run (module-level singleton)
let moduleBootstrapRan = false;
let lastBootstrapPath: string | null = null;

/**
 * Check if the current route is a module route
 */
export function isModuleRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/module/');
}

/**
 * Check if the current route is a FreeWorld route
 */
export function isFreeWorldRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return path.startsWith('/freeworld') ||
         path.startsWith('/loppuhaaste') ||
         path.startsWith('/final-challenge') ||
         path.startsWith('/city') || 
         path === '/dashboard' ||
         path === '/student';
}

/**
 * Hook that ensures minimal bootstrap for module routes
 * Does NOT trigger FreeWorld data loading
 */
export const useModuleBootstrap = () => {
  const { playerSession, loading: authLoading } = usePlayerAuth();
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    // Only run on module routes
    if (!isModuleRoute()) {
      return;
    }

    // Skip if already bootstrapped for this path
    const currentPath = window.location.pathname;
    if (moduleBootstrapRan && lastBootstrapPath === currentPath) {
      if (import.meta.env.DEV) {
        console.debug('[ModuleBootstrap] skipping - already ran for', currentPath);
      }
      return;
    }

    // Skip if auth is still loading or no session
    if (authLoading || !playerSession?.playerId) {
      return;
    }

    // Mark as bootstrapped
    moduleBootstrapRan = true;
    lastBootstrapPath = currentPath;
    bootstrappedRef.current = true;

    if (import.meta.env.DEV) {
      console.log('[ModuleBootstrap] completed for', currentPath, {
        playerId: playerSession.playerId.slice(0, 8) + '...',
      });
    }

    // Module bootstrap is minimal - no FreeWorld data loaded
    // Quiz progress is loaded by useQuizProgress when needed

  }, [authLoading, playerSession?.playerId]);

  return {
    isReady: !authLoading && !!playerSession?.playerId,
    playerId: playerSession?.playerId ?? null,
    sessionToken: playerSession?.sessionToken ?? null,
  };
};

/**
 * Reset module bootstrap state (for testing/HMR)
 */
export function resetModuleBootstrap(): void {
  moduleBootstrapRan = false;
  lastBootstrapPath = null;
}
